import React, { useId } from 'react';

// Le petit Nexus : une tête ronde à antenne, un visage sur une visière, une expression par humeur.
//   idle        il suit la carte (clignements, léger flottement)
//   sleeping    rien à faire depuis un moment (yeux fermés, « z »)
//   waiting     il va relire cet élément (en file)
//   reading     Kev relit (les yeux balaient)
//   linking     il cherche ou fait un lien (maillon)
//   writing     Muse écrit (yeux en l'air, bulle qui tape)
//   happy       c'est bon, ou un lien est fait (yeux plissés, sourire, petit saut)
//   suggesting  il a une proposition (sourcil levé, étincelle)
//   asking      il a une question (tête penchée, « ? »)
//   error       il n'a pas pu (yeux ronds, bouche plate)

const CSS = `
@keyframes nx-bob { 0%,100% { transform: translateY(0) } 50% { transform: translateY(-1.6px) } }
@keyframes nx-blink { 0%, 93%, 100% { transform: scaleY(1) } 96% { transform: scaleY(.12) } }
@keyframes nx-scan { 0% { transform: translateX(-1.6px) } 100% { transform: translateX(1.6px) } }
@keyframes nx-bounce { 0%,100% { transform: translateY(0) } 25% { transform: translateY(-5px) } 50% { transform: translateY(0) } 70% { transform: translateY(-2px) } }
@keyframes nx-tilt { 0%,100% { transform: rotate(-10deg) } 50% { transform: rotate(-6deg) } }
@keyframes nx-dot { 0%, 60%, 100% { opacity: .25; transform: translateY(0) } 30% { opacity: 1; transform: translateY(-1.6px) } }
@keyframes nx-glow { 0%,100% { opacity: .55; transform: scale(.85) } 50% { opacity: 1; transform: scale(1.2) } }
@keyframes nx-z { 0% { opacity: 0; transform: translate(0,2px) scale(.7) } 30% { opacity: 1 } 100% { opacity: 0; transform: translate(5px,-8px) scale(1.1) } }
@keyframes nx-ring { 0% { transform: scale(.5); opacity: .8 } 100% { transform: scale(1.9); opacity: 0 } }
@keyframes nx-pop { 0% { transform: scale(.3); opacity: 0 } 70% { transform: scale(1.12); opacity: 1 } 100% { transform: scale(1); opacity: 1 } }
@keyframes nx-spin { to { transform: rotate(360deg) } }
.nx-fill { transform-box: fill-box; transform-origin: center }
.nx-bob { animation: nx-bob 2.8s ease-in-out infinite }
.nx-bob-slow { animation: nx-bob 4.5s ease-in-out infinite }
.nx-blink { animation: nx-blink 4.2s ease-in-out infinite }
.nx-scan { animation: nx-scan .7s ease-in-out infinite alternate }
.nx-bounce { animation: nx-bounce .9s ease-out 2 }
.nx-tilt { animation: nx-tilt 2.4s ease-in-out infinite }
.nx-dot { animation: nx-dot 1.1s ease-in-out infinite }
.nx-glow { animation: nx-glow 1.2s ease-in-out infinite }
.nx-z { animation: nx-z 2.4s ease-out infinite }
.nx-ring { animation: nx-ring 1.1s ease-out 2 }
.nx-pop { animation: nx-pop .35s cubic-bezier(.2,.9,.3,1.3) both }
.nx-spin { animation: nx-spin 2.4s linear infinite }
@media (prefers-reduced-motion: reduce) { .nx-bob,.nx-bob-slow,.nx-blink,.nx-scan,.nx-bounce,.nx-tilt,.nx-dot,.nx-glow,.nx-z,.nx-ring,.nx-spin { animation: none } }
`;

let styled = false;
function ensureStyle() {
  if (styled || typeof document === 'undefined') return;
  styled = true;
  const tag = document.createElement('style');
  tag.setAttribute('data-nexus-bot', '');
  tag.textContent = CSS;
  document.head.appendChild(tag);
}

const INK = '#1e1b4b';
const MOUTH = '#4338ca';

function Eyes({ mood }) {
  const left = 17.5;
  const right = 26.5;
  const y = 23;
  if (mood === 'happy') {
    return (
      <g fill="none" stroke={INK} strokeWidth="1.9" strokeLinecap="round">
        <path d={`M${left - 2} ${y + 0.8} q2 -2.8 4 0`} />
        <path d={`M${right - 2} ${y + 0.8} q2 -2.8 4 0`} />
      </g>
    );
  }
  if (mood === 'sleeping') {
    return (
      <g fill="none" stroke={INK} strokeWidth="1.7" strokeLinecap="round">
        <path d={`M${left - 2} ${y} q2 1.8 4 0`} />
        <path d={`M${right - 2} ${y} q2 1.8 4 0`} />
      </g>
    );
  }
  if (mood === 'waiting') {
    return (
      <g fill="none" stroke={INK} strokeWidth="1.8" strokeLinecap="round">
        <path d={`M${left - 1.8} ${y + 0.4} h3.6`} />
        <path d={`M${right - 1.8} ${y + 0.4} h3.6`} />
      </g>
    );
  }
  if (mood === 'error') {
    return (
      <g fill={INK}>
        <circle cx={left} cy={y} r="1.3" />
        <circle cx={right} cy={y} r="1.3" />
      </g>
    );
  }
  const look = { writing: [1, -1.4], asking: [-0.8, -1.2], linking: [1.3, 0], suggesting: [0.6, -0.4] }[mood] || [0, 0];
  const eye = (cx) => <ellipse cx={cx + look[0]} cy={y + look[1]} rx="1.9" ry="2.4" fill={INK} />;
  return (
    <g className={`nx-fill ${mood === 'reading' ? 'nx-scan' : 'nx-blink'}`}>
      {eye(left)}
      {eye(right)}
      {/* reflets */}
      <circle cx={left + look[0] + 0.7} cy={y + look[1] - 0.9} r="0.55" fill="#fff" />
      <circle cx={right + look[0] + 0.7} cy={y + look[1] - 0.9} r="0.55" fill="#fff" />
      {mood === 'suggesting' && <path d={`M${right - 2.4} ${y - 4.4} q2.4 -1.6 4.8 -0.2`} fill="none" stroke={INK} strokeWidth="1.2" strokeLinecap="round" />}
    </g>
  );
}

function Mouth({ mood }) {
  const common = { fill: 'none', stroke: MOUTH, strokeWidth: 1.5, strokeLinecap: 'round' };
  if (mood === 'happy') return <path d="M18.6 26.6 q3.4 3.4 6.8 0" {...common} strokeWidth={1.7} />;
  if (mood === 'asking' || mood === 'linking') return <ellipse cx="22" cy="27.4" rx="1.3" ry="1.1" fill={MOUTH} />;
  if (mood === 'error') return <path d="M19.4 27.8 q1.3 -1 2.6 0 t2.6 0" {...common} />;
  if (mood === 'writing' || mood === 'reading' || mood === 'sleeping' || mood === 'waiting') return <path d="M20.2 27.4 h3.6" {...common} />;
  return <path d="M19.6 26.8 q2.4 1.9 4.8 0" {...common} />;
}

function Accessory({ mood, uid }) {
  if (mood === 'asking') {
    return (
      <g className="nx-pop" style={{ transformOrigin: '40px 9px' }}>
        <circle cx="40" cy="9" r="6.4" fill="#fff" stroke="#c7d2fe" strokeWidth="1.2" />
        <path d="M35.6 13.4 l-2.4 2.6 l3.6 -1" fill="#fff" stroke="#c7d2fe" strokeWidth="1" strokeLinejoin="round" />
        <text x="40" y="12.4" textAnchor="middle" fontSize="9.5" fontWeight="800" fill="#4f46e5" fontFamily="system-ui, sans-serif">?</text>
      </g>
    );
  }
  if (mood === 'suggesting') {
    return (
      <g className="nx-fill nx-glow">
        <path d="M40 2.6 l1.6 4.8 l4.8 1.6 l-4.8 1.6 l-1.6 4.8 l-1.6 -4.8 l-4.8 -1.6 l4.8 -1.6 z" fill="#fbbf24" stroke="#f59e0b" strokeWidth=".6" strokeLinejoin="round" />
      </g>
    );
  }
  if (mood === 'sleeping') {
    return (
      <g className="nx-fill nx-z">
        <text x="37" y="12" fontSize="8" fontWeight="800" fill="#818cf8" fontFamily="system-ui, sans-serif">z</text>
      </g>
    );
  }
  if (mood === 'writing') {
    return (
      <g>
        <rect x="34" y="31.5" width="13" height="7.5" rx="3.75" fill="#fff" stroke="#c7d2fe" strokeWidth="1" />
        {[37.4, 40.5, 43.6].map((cx, i) => (
          <circle key={cx} className="nx-fill nx-dot" style={{ animationDelay: `${i * 0.16}s` }} cx={cx} cy="35.25" r="1.15" fill="#6366f1" />
        ))}
      </g>
    );
  }
  if (mood === 'linking') {
    return (
      <g className="nx-fill nx-glow" fill="none" stroke="#10b981" strokeWidth="1.7" strokeLinecap="round">
        <path d="M37.5 24.5 l2.2 -2.2 a2.6 2.6 0 0 1 3.7 3.7 l-2.2 2.2" />
        <path d="M43.2 29.4 l-2.2 2.2 a2.6 2.6 0 0 1 -3.7 -3.7 l2.2 -2.2" transform="translate(-0.6 -2.6)" />
      </g>
    );
  }
  if (mood === 'happy') {
    return (
      <g>
        <circle className="nx-fill nx-ring" cx="22" cy="25" r="15" fill="none" stroke={`url(#${uid}-ring)`} strokeWidth="1.6" />
        <path d="M41 6 l1 3 l3 1 l-3 1 l-1 3 l-1 -3 l-3 -1 l3 -1 z" fill="#34d399" className="nx-fill nx-glow" />
      </g>
    );
  }
  if (mood === 'reading') {
    return (
      <g className="nx-fill nx-glow" style={{ animationDuration: '.9s' }}>
        <circle cx="40.5" cy="10" r="1.4" fill="#a5b4fc" />
      </g>
    );
  }
  return null;
}

/** Le petit Nexus. size en pixels ; mood : voir plus haut ; title : bulle d'aide du navigateur. */
export default function NexusBot({ mood = 'idle', size = 28, title, className = '' }) {
  ensureStyle();
  const uid = `nx${useId().replace(/[^a-zA-Z0-9]/g, '')}`;
  const busy = ['reading', 'linking', 'writing'].includes(mood);
  const bodyMotion = mood === 'happy' ? 'nx-bounce' : mood === 'asking' ? 'nx-tilt' : mood === 'sleeping' ? 'nx-bob-slow' : 'nx-bob';
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" className={`shrink-0 overflow-visible ${className}`} role="img" aria-label={title || 'Nexus'}>
      {title && <title>{title}</title>}
      <defs>
        <linearGradient id={`${uid}-head`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#818cf8" />
          <stop offset="55%" stopColor="#6366f1" />
          <stop offset="100%" stopColor="#8b5cf6" />
        </linearGradient>
        <linearGradient id={`${uid}-ring`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#34d399" />
          <stop offset="100%" stopColor="#818cf8" />
        </linearGradient>
        <radialGradient id={`${uid}-tip`}>
          <stop offset="0%" stopColor="#fef3c7" />
          <stop offset="100%" stopColor={busy ? '#f59e0b' : '#a5b4fc'} />
        </radialGradient>
      </defs>
      <g className={`nx-fill ${bodyMotion}`} style={mood === 'asking' ? { transformOrigin: '22px 38px', transformBox: 'view-box' } : undefined}>
        {/* antenne */}
        <path d="M22 12.5 V6.8" stroke="#6366f1" strokeWidth="1.8" strokeLinecap="round" />
        <circle className={busy ? 'nx-fill nx-glow' : ''} cx="22" cy="5.4" r={busy ? 2.6 : 2.2} fill={`url(#${uid}-tip)`} />
        {/* tête */}
        <rect x="8" y="12" width="28" height="26" rx="11" fill={`url(#${uid}-head)`} />
        <rect x="8" y="12" width="28" height="26" rx="11" fill="none" stroke="#4f46e5" strokeOpacity=".35" />
        <ellipse cx="15" cy="15.6" rx="4.2" ry="1.6" fill="#fff" opacity=".28" />
        {/* oreilles */}
        <rect x="5.6" y="21" width="3" height="7" rx="1.5" fill="#6366f1" />
        <rect x="35.4" y="21" width="3" height="7" rx="1.5" fill="#6366f1" />
        {/* visière */}
        <rect x="11.5" y="17" width="21" height="14" rx="7" fill="#eef2ff" />
        {mood === 'happy' && (
          <g fill="#f9a8d4" opacity=".7">
            <ellipse cx="14.6" cy="27.4" rx="1.7" ry="1" />
            <ellipse cx="29.4" cy="27.4" rx="1.7" ry="1" />
          </g>
        )}
        <Eyes mood={mood} />
        <Mouth mood={mood} />
      </g>
      <Accessory mood={mood} uid={uid} />
    </svg>
  );
}

/** Humeur de Nexus pour un élément, d'après son état côté serveur (et ce qu'il fait en ce moment). */
export function moodOf(state, working) {
  if (!state) return null;
  if (working) return working.mood || 'reading';
  if (state.status === 'thinking') return 'reading';
  if (state.status === 'pending') return 'waiting';
  if (state.status === 'question') return 'asking';
  if (state.status === 'suggestion') return 'suggesting';
  if (state.status === 'error') return 'error';
  return null;
}
